import { ChangeDetectorRef, Component, effect, inject, input, Input, OnChanges, OnDestroy, SimpleChanges, forwardRef, OnInit } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { Subject, Subscription, takeUntil, tap } from 'rxjs';
import { ISvcChipGroupedOption, ISvcChipOption } from '@solvace/component/svc-controls';
import { ModalSelectAreaOrLocationComponent } from './modal-select-location/modal-select-area-or-location.component';
import { SvcLocationsService } from './service/svc-location.service';
import { Uda } from './service/uda.model';

@Component({
  selector: 'svc-location-field',
  templateUrl: './svc-location-field.component.html',
  styleUrls: ['./svc-location-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SvcLocationFieldComponent),
      multi: true,
    },
  ],
})
export class SvcLocationFieldComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {
  @Input() label: string;
  @Input() formControl: FormControl;
  @Input() readonly: boolean = false;
  @Input() showValidation: boolean = true;
  @Input() fallbackTextValue = '-';
  @Input() placeholder: string;
  @Input() inputId = 'svc-location-field-locations';
  public canSelectOnly = input<'all' | 'location' | 'area'>('all');

  public isLoading = true;
  protected areas: Uda[] = [];
  protected locations: Uda[] = [];
  protected options: ISvcChipGroupedOption[] = [];

  #destroy = new Subject();
  #formControlSubscription: Subscription;
  #matDialog = inject(MatDialog);
  #locationService = inject(SvcLocationsService);
  #translocoService = inject(TranslocoService);
  // Default-CD component that may render under an OnPush host; its area/location options load
  // asynchronously into plain fields, so mark for check when they resolve or the field stays on its
  // loading state until an unrelated click ticks change detection (AB#69059).
  #cdr = inject(ChangeDetectorRef);

  #othersName: string = this.#translocoService.translate('Outros');
  #canSelectArea = true;
  #canSelectLocation = true;

  constructor() {
    this.#onChangesCanSelectOnly();
  }
  // ControlValueAccessor internals
  private _onChange: (value: any) => void = () => {};
  private _onTouched: () => void = () => {};
  private _writingValue = false;
  private _isInternalControl = false;
  private _disabled = false;

  #onChangesCanSelectOnly(): void {
    effect(() => {
      if (this.canSelectOnly()) this.#setCanSelect();
    });
  }

  #setCanSelect(): void {
    const canSelectOnly = this.canSelectOnly();
    this.#canSelectArea = canSelectOnly !== 'location';
    this.#canSelectLocation = canSelectOnly !== 'area';
  }

  public ngOnInit(): void {
    this.#setCanSelect();
    this.#locationService.initialOptions$
      .pipe(
        takeUntil(this.#destroy),
        tap((initialOptions) => {
          this.areas = initialOptions.areas;
          this.locations = initialOptions.locations;
          this.options = [
            ...(this.#canSelectArea
              ? [
                  {
                    group: this.#translocoService.translate('Áreas'),
                    options: this.areas.map(
                      (a) =>
                        <ISvcChipOption>{
                          label: a.udaName,
                          value: a.udaId,
                          data: a,
                        }
                    ),
                  },
                ]
              : []),
            ...(this.#canSelectLocation
              ? [
                  {
                    group: this.#translocoService.translate('Localidades'),
                    options: this.locations.map(
                      (l) =>
                        <ISvcChipOption>{
                          label: l.udaName,
                          value: l.udaId,
                          data: l,
                        }
                    ),
                  },
                ]
              : []),
          ];
          this.#cdr.markForCheck();
        })
      )
      .subscribe();
    this.#locationService.initialOptionsIsLoading$
      .pipe(
        takeUntil(this.#destroy),
        tap((loading) => {
          this.isLoading = loading;
          this.#cdr.markForCheck();
        })
      )
      .subscribe();
    this.#locationService.getInitialOptions();
    // ensure we listen to the control; if none provided, create an internal one
    if (!this.formControl) {
      this.formControl = new FormControl(null);
      this._isInternalControl = true;
    }
    this.#listenFormControl();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if ('formControl' in changes) {
      this._isInternalControl = !this.formControl;
      this.#listenFormControl();
    }
  }

  public openDialog() {
    if (this.isLoading || this.formControl?.disabled) {
      return;
    }
    this.#matDialog
      .open(ModalSelectAreaOrLocationComponent, {
        data: { canSelectArea: this.#canSelectArea, canSelectLocation: this.#canSelectLocation, label: this.label },
        panelClass: ['p-4'],
      })
      .afterClosed()
      .subscribe((location: { id: number; text: string }) => {
        if (location) {
          this.options = this.options.filter((opt) => opt.group !== this.#othersName);
          if (!this.areas.some((a) => a.udaId === location.id) && !this.locations.some((l) => l.udaId === location.id)) {
            this.options = [
              this.options.length > 0 ? this.options[0] : null,
              this.options.length > 1 ? this.options[1] : null,
              {
                group: this.#othersName,
                options: [{ value: location.id, label: location.text }],
              },
            ].filter((g) => g !== null);
          }

          this.#formControlSubscription?.unsubscribe();
          setTimeout(() => {
            this.formControl?.setValue([location.id]);
            this.#listenFormControl();
            if (this._isInternalControl) this._onChange([location.id]);
          });
        }
      });
  }

  #listenFormControl() {
    this.#formControlSubscription?.unsubscribe();
    this.#formControlSubscription = this.formControl?.valueChanges
      .pipe(
        tap((v) => {
          this.options = this.options.filter((opt) => opt.group !== this.#othersName);
          // AB#75359: a bound [formControl] is the same control the forms directive drives, so forwarding its
          // valueChanges to _onChange re-sets it and recurses until the stack overflows (e.g. on form.disable()).
          if (this._isInternalControl && !this._writingValue) {
            this._onChange(v);
          }
        })
      )
      .subscribe();
  }

  public ngOnDestroy(): void {
    this.#destroy?.next(null);
    this.#destroy?.complete();
    this.#formControlSubscription?.unsubscribe();
  }
  // ControlValueAccessor implementation
  writeValue(obj: any): void {
    if (!this.formControl) {
      this.formControl = new FormControl(obj);
      this._isInternalControl = true;
      this.#listenFormControl();
      return;
    }
    if (!this._isInternalControl) return;
    if (JSON.stringify(this.formControl.value) === JSON.stringify(obj)) return;
    this._writingValue = true;
    try {
      this.formControl.setValue(obj);
    } finally {
      this._writingValue = false;
    }
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this._disabled = isDisabled;
    if (!this.formControl || !this._isInternalControl) return;
    if (this.formControl.disabled !== isDisabled) {
      if (isDisabled) {
        this.formControl.disable({ emitEvent: false });
      } else {
        this.formControl.enable({ emitEvent: false });
      }
    }
  }
}
